import * as React from 'react';

import { Loader } from '../../components/Loader/Loader';
import { Bem } from '../../service/Bem/Bem';
import { PopupScopeContext } from '../../service/Dom/Dom';
import { PcodeAds } from '../../typings/pcode';
import { AddAdaptiveCreativeContainer } from '../AddCreativePage/AddAdaptiveCreative/AddAdaptiveCreativeContainer';
import { AddCommonCreativeContainer } from '../AddCreativePage/AddCommonCreative/AddCommonCreativeContainer';
import { AddOverlayCreativeContainer } from '../AddCreativePage/AddOverlayCreative/AddOverlayCreativeContainer';
import { AddVideoCreativeContainer } from '../AddCreativePage/AddVideoCreative/AddVideoCreativeContainer';
import { isAdaptiveCreative, isCreateImmediately, isOverlay, isVideoAddition } from '../common/helpers';
import { CanvasAppContext, CreativeType, Page, SharedData } from '../common/typings';
import { CommonCreativesContainer } from '../CommonCreatives/CommonCreativesContainer';
import { IdeasListContainer } from '../IdeasList/IdeasListContainer';
import { PageToolbar } from '../PageToolbar/PageToolbar';
import { PresetsContainer } from '../Presets/PresetsContainer';
import { VideoCreativesContainer } from '../VideoCreatives/VideoCreativesContainer';

import './CanvasApp.css';

export interface CanvasAppProps {
    page: Page;
    sharedData: SharedData;
    isBusy: boolean;
    showBusy: () => void;
    hideBusy: () => void;
    onClose: () => void;
    onCreativesSelect: (ids: number[]) => void;
    changePage: (page: Page) => void;
    openConstructor: (page: Page) => void;

    videoPreviewData?: PcodeAds;
}

function getPages(sharedData: SharedData): Page[] {
    const pages = [Page.CREATIVES, Page.PRESETS];

    if (sharedData.creativeType === CreativeType.COMMON) {
        pages.push(Page.IDEAS);
    }

    return pages;
}

const BLOCK = 'canvas-app';

export class CanvasApp extends React.PureComponent<CanvasAppProps> {
    private ref = React.createRef<HTMLDivElement>();

    renderPage(page: Page) {
        const { onClose, onCreativesSelect, videoPreviewData, sharedData } = this.props;
        const isVideo = isVideoAddition(sharedData.creativeType);
        const isAdaptive = isAdaptiveCreative(sharedData.creativeType);

        switch (page) {
            case Page.CONSTRUCTOR:
                if (isVideo) {
                    return (
                        <AddVideoCreativeContainer
                            videoPreviewData={videoPreviewData}
                            creativesSelect={onCreativesSelect}
                            onCancel={onClose}
                        />
                    );
                } else if (isOverlay(this.props.sharedData.creativeType)) {
                    return <AddOverlayCreativeContainer creativesSelect={onCreativesSelect} onCancel={onClose} />;
                }

                if (isAdaptive) {
                    return <AddAdaptiveCreativeContainer onCancel={onClose} />;
                }

                return <AddCommonCreativeContainer onCancel={onClose} />;

            case Page.PRESETS:
                return <PresetsContainer />;

            case Page.CREATIVES:
                if (isVideo) {
                    return (
                        <VideoCreativesContainer
                            videoPreviewData={videoPreviewData}
                            creativesSelect={onCreativesSelect}
                            cancel={onClose}
                        />
                    );
                }

                return <CommonCreativesContainer creativesSelect={onCreativesSelect} cancel={onClose} />;

            case Page.IDEAS:
                return <IdeasListContainer />;

            default:
                throw new Error(`Unknown page: ${page}`);
        }
    }

    renderNavigation = (): React.ReactNode => {
        const { page, sharedData } = this.props;

        return (
            <PageToolbar
                activeTab={page}
                pages={getPages(sharedData)}
                isCreate={isCreateImmediately(sharedData)}
                changePage={this.selectTab}
            />
        );
    };

    render() {
        const { page } = this.props;
        const isConstructor = page === Page.CONSTRUCTOR;
        return (
            <div className={BLOCK} ref={this.ref}>
                <CanvasAppContext.Provider value={{ parent: this.getRef }}>
                    <PopupScopeContext.Provider value={this.getRef}>
                        {this.props.isBusy && <Loader size={'s'} />}
                        {!isConstructor && this.renderNavigation()}
                        <div
                            className={Bem.cls(BLOCK, 'page', {
                                constructor: isConstructor
                            })}
                        >
                            {this.renderPage(page)}
                        </div>
                    </PopupScopeContext.Provider>
                </CanvasAppContext.Provider>
            </div>
        );
    }

    private getRef = (): HTMLElement => (this.ref.current ? this.ref.current : document.body);

    private selectTab = (page: Page) => {
        const { sharedData, openConstructor, changePage } = this.props;

        if (page === Page.PRESETS && isCreateImmediately(sharedData)) {
            openConstructor(Page.CREATIVES);
        } else {
            changePage(page);
        }
    };
}
